import { useState } from "react";
import { Calendar, FileText, AlertCircle } from "lucide-react";
import { Plus, X } from "lucide-react";
import PermissionInput from "../ui/PermissionInput";
import ToggleSwitch from "../ui/ToggleSwitch";
import { PALETTE } from "../../styles/configStyles";
import EffectiveDateBadge from "../EffectiveDateBadge";
import {
  validateNumericText,
  validateYearRange,
  restrictToNumericKeys,
  restrictPasteToNumeric,
} from "../../utils/textInputValidation";

const LeavesTab = ({
  styles,
  t,
  theme,
  isRTL,
  language,
  config,
  setConfig,
  canEditField,
  getFieldStyle,
  isCreatingNewConfig,
  isEditingArchive,
  selectedArchiveId,
  archivedConfigs,
  unlockTab,
  resetTabToGlobal,
  getTabEffectiveDate,
  getBadgeMode,
  loading,
}) => {
  const canEdit = canEditField("leaves") && !loading;

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  const departmentRules =
    config && typeof config.departmentRules === "object" && config.departmentRules !== null
      ? config.departmentRules
      : {};

  const getAttendanceValue = (field) => {
    if (
      config.department &&
      departmentRules &&
      typeof departmentRules === "object" &&
      Object.prototype.hasOwnProperty.call(departmentRules, config.department) &&
      departmentRules[config.department]
    ) {
      const deptRules = departmentRules[config.department];
      return deptRules && deptRules[field] !== undefined ? deptRules[field] : config[field];
    }
    return config[field];
  };

  const handleAttendanceChange = (field, value) => {
    if (config.department) {
      if (isEditingArchive && selectedArchiveId && archivedConfigs.length > 0) {
        setConfig((prev) => ({
          ...prev,
          departmentRules: {
            ...prev.departmentRules,
            [prev.department]: {
              ...(prev.departmentRules[prev.department] || {}),
              [field]: value,
            },
          },
        }));
      } else {
        setConfig((prev) => ({
          ...prev,
          [field]: value,
          departmentRules: {
            ...prev.departmentRules,
            [prev.department]: {
              ...(prev.departmentRules[prev.department] || {}),
              [field]: value,
            },
          },
        }));
      }
    } else {
      setConfig((prev) => ({ ...prev, [field]: value }));
    }
  };

  return (
    <>
      {/* Loading overlay */}
      {loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: theme === "dark" ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.05)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: "12px",
          }}
        >
          <div
            style={{
              padding: "20px 30px",
              background: theme === "dark" ? "#1e293b" : "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div className="spinner" />
            <span style={{ fontWeight: 600, color: theme === "dark" ? "#f1f5f9" : "#0f172a" }}>
              {isRTL ? "جاري الحفظ..." : "Saving..."}
            </span>
          </div>
        </div>
      )}

      {/* Validation error toast */}
      {Object.keys(validationErrors).length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: isRTL ? "auto" : "24px",
            left: isRTL ? "24px" : "auto",
            background: theme === "dark" ? "#1e293b" : "#ffffff",
            border: `2px solid #ef4444`,
            borderRadius: "12px",
            padding: "16px 20px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            zIndex: 1000,
            maxWidth: "400px",
            animation: "slideUp 0.3s ease",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
            <AlertCircle size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: "2px" }} />
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: "14px",
                  fontWeight: 700,
                  color: theme === "dark" ? "#f1f5f9" : "#0f172a",
                  marginBottom: "6px",
                }}
              >
                {isRTL ? "يرجى تصحيح الأخطاء التالية:" : "Please fix the following errors:"}
              </h4>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: isRTL ? "0" : "20px",
                  paddingRight: isRTL ? "20px" : "0",
                  fontSize: "13px",
                  color: theme === "dark" ? "rgba(226,232,240,0.8)" : "#64748b",
                }}
              >
                {Object.values(validationErrors)
                  .slice(0, 3)
                  .map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                {Object.keys(validationErrors).length > 3 && (
                  <li>
                    {isRTL
                      ? `و ${Object.keys(validationErrors).length - 3} أخطاء أخرى`
                      : `and ${Object.keys(validationErrors).length - 3} more...`}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: "24px", marginBottom: "24px", position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>
            <div style={styles.sectionIconBadge(PALETTE.emerald.base)}>
              <Calendar size={20} />
            </div>
            {t.leaves.title}
            {!canEdit && (
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  marginLeft: isRTL ? "0" : "12px",
                  marginRight: isRTL ? "12px" : "0",
                  padding: "4px 12px",
                  borderRadius: "6px",
                  background: `${PALETTE.amber.base}15`,
                  color: PALETTE.amber.base,
                  border: `1px solid ${PALETTE.amber.base}25`,
                }}
              >
                {language === "ar" ? "(للقراءة فقط)" : "(Read-only)"}
              </span>
            )}
          </h3>
          <EffectiveDateBadge
            date={getTabEffectiveDate ? getTabEffectiveDate("leave") : config.leaveEffectiveDate}
            isRTL={isRTL}
            theme={theme}
            mode={getBadgeMode ? getBadgeMode("leave") : "locked"}
            onUnlock={unlockTab ? () => unlockTab("leave") : undefined}
            onReset={resetTabToGlobal ? () => resetTabToGlobal("leave") : undefined}
            onDateChange={(date) => setConfig((prev) => ({ ...prev, leaveEffectiveDate: date }))}
          />
        </div>

        <p style={{
          color: theme === "dark" ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.75)",
          marginBottom: "20px",
          fontSize: "clamp(13px, 2vw, 14px)",
          textAlign: isRTL ? "right" : "left",
        }}>
          {t.leaves.progressiveLeave}
        </p>

        {/* Half Day Request Threshold */}
        <div style={styles.contentSection}>
          <h3 style={styles.sectionTitle}>
            <div style={styles.sectionIconBadge(PALETTE.amber.base)}>
              <AlertCircle size={20} />
            </div>
            {t.attendance.halfDayRequestThreshold}
          </h3>

          <p style={{
            color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(15,23,42,0.7)",
            fontSize: "13px",
            marginBottom: "16px",
            lineHeight: "1.6",
          }}>
            {t.attendance.halfDayRequestThresholdDesc}
          </p>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.attendance.halfDayRequestThreshold}</label>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <PermissionInput
                  section="attendance"
                  type="number"
                  min="0.5"
                  max="24"
                  step="0.5"
                  value={getAttendanceValue("halfDayRequestThreshold") ?? ""}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : parseFloat(e.target.value);
                    if (val === null || (!isNaN(val) && val >= 0.5 && val <= 24)) {
                      handleAttendanceChange("halfDayRequestThreshold", val);
                    }
                  }}
                  style={{ ...getFieldStyle("attendance", styles.input), flex: 1 }}
                  placeholder="اختياري / Optional"
                  canEditField={canEditField}
                  getFieldStyle={getFieldStyle}
                />
                <span style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(15,23,42,0.8)",
                  minWidth: "60px",
                  textAlign: "center",
                }}>
                  {isRTL ? "ساعات" : "hours"}
                </span>
              </div>
              <p style={{
                fontSize: "12px",
                color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(15,23,42,0.5)",
                marginTop: "8px",
                fontStyle: "italic",
              }}>
                {isRTL
                  ? "القيمة الحالية: " + (getAttendanceValue("halfDayRequestThreshold") || "لم يتم التعيين") + " " + (getAttendanceValue("halfDayRequestThreshold") ? "ساعات" : "")
                  : "Current value: " + (getAttendanceValue("halfDayRequestThreshold") || "Not set") + " " + (getAttendanceValue("halfDayRequestThreshold") ? "hours" : "")}
              </p>
            </div>
          </div>
        </div>

        {/* Annual Leave Rules Section */}
        <div style={styles.contentSection}>
          <h3 style={styles.sectionTitle}>
            <div style={styles.sectionIconBadge(PALETTE.primary.base)}>
              <Calendar size={20} />
            </div>
            {isRTL ? "قواعد الإجازة السنوية التصاعدية" : "Progressive Annual Leave Rules"}
          </h3>

          <p
            style={{
              color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(15,23,42,0.7)",
              fontSize: "13px",
              marginBottom: "20px",
              lineHeight: "1.6",
            }}
          >
            {isRTL
              ? "حدد عدد أيام الإجازة السنوية بناءً على سنوات الخدمة. اترك 'إلى السنة' فارغاً للإشارة إلى عدد غير محدود."
              : "Define annual leave days based on years of service. Leave 'To Year' empty to indicate unlimited."}
          </p>

          {(config.annualLeaveRules || []).map((rule, index) => (
            <div key={index} style={styles.formRow}>
              {/* From Year */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.leaves.fromYear}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={rule.yearsFrom}
                  disabled={!canEdit}
                  onChange={(e) => {
                    if (!canEdit) return;

                    const validation = validateNumericText(e.target.value, {
                      min: 0,
                      max: 100,
                      fieldName: isRTL ? "من السنة" : "From Year",
                      allowEmpty: false,
                    });

                    if (validation.isValid) {
                      const newRules = [...config.annualLeaveRules];
                      newRules[index].yearsFrom = validation.sanitized;
                      setConfig({ ...config, annualLeaveRules: newRules });

                      // Clear error
                      setValidationErrors((prev) => {
                        const updated = { ...prev };
                        delete updated[`fromYear_${index}`];
                        return updated;
                      });

                      // Validate range with toYear
                      const rangeValidation = validateYearRange(validation.sanitized, newRules[index].yearsTo);
                      if (!rangeValidation.isValid) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          [`yearRange_${index}`]: isRTL
                            ? "يجب أن تكون 'من السنة' أقل من 'إلى السنة'"
                            : rangeValidation.error,
                        }));
                      } else {
                        setValidationErrors((prev) => {
                          const updated = { ...prev };
                          delete updated[`yearRange_${index}`];
                          return updated;
                        });
                      }
                    } else {
                      setValidationErrors((prev) => ({
                        ...prev,
                        [`fromYear_${index}`]: validation.error,
                      }));
                    }
                  }}
                  onKeyDown={restrictToNumericKeys}
                  onPaste={restrictPasteToNumeric}
                  style={{
                    ...getFieldStyle("leaves", styles.input),
                    borderColor:
                      validationErrors[`fromYear_${index}`] || validationErrors[`yearRange_${index}`]
                        ? "#ef4444"
                        : undefined,
                  }}
                  placeholder="0"
                  aria-label={t.leaves.fromYear}
                  aria-invalid={!!validationErrors[`fromYear_${index}`]}
                  aria-describedby={validationErrors[`fromYear_${index}`] ? `error-fromYear-${index}` : undefined}
                />
                {validationErrors[`fromYear_${index}`] && (
                  <div
                    id={`error-fromYear-${index}`}
                    role="alert"
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "6px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <AlertCircle size={14} />
                    {validationErrors[`fromYear_${index}`]}
                  </div>
                )}
              </div>

              {/* To Year */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.leaves.toYear}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={rule.yearsTo === null ? "" : rule.yearsTo}
                  onChange={(e) => {
                    if (!canEdit) return;

                    // Allow empty for "unlimited"
                    if (e.target.value === "") {
                      const newRules = [...config.annualLeaveRules];
                      newRules[index].yearsTo = null;
                      setConfig({ ...config, annualLeaveRules: newRules });
                      setValidationErrors((prev) => {
                        const updated = { ...prev };
                        delete updated[`toYear_${index}`];
                        delete updated[`yearRange_${index}`];
                        return updated;
                      });
                      return;
                    }

                    const validation = validateNumericText(e.target.value, {
                      min: 0,
                      max: 100,
                      fieldName: isRTL ? "إلى السنة" : "To Year",
                      allowEmpty: true,
                    });

                    if (validation.isValid) {
                      const newRules = [...config.annualLeaveRules];
                      newRules[index].yearsTo = validation.sanitized;
                      setConfig({ ...config, annualLeaveRules: newRules });

                      // Validate range
                      const rangeValidation = validateYearRange(newRules[index].yearsFrom, validation.sanitized);

                      if (!rangeValidation.isValid) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          [`yearRange_${index}`]: isRTL
                            ? "يجب أن تكون 'من السنة' أقل من 'إلى السنة'"
                            : rangeValidation.error,
                        }));
                      } else {
                        setValidationErrors((prev) => {
                          const updated = { ...prev };
                          delete updated[`yearRange_${index}`];
                          delete updated[`toYear_${index}`];
                          return updated;
                        });
                      }
                    } else {
                      setValidationErrors((prev) => ({
                        ...prev,
                        [`toYear_${index}`]: validation.error,
                      }));
                    }
                  }}
                  onKeyDown={restrictToNumericKeys}
                  onPaste={restrictPasteToNumeric}
                  disabled={!canEdit}
                  placeholder={t.leaves.unlimited}
                  style={{
                    ...getFieldStyle("leaves", styles.input),
                    borderColor:
                      validationErrors[`toYear_${index}`] || validationErrors[`yearRange_${index}`]
                        ? "#ef4444"
                        : undefined,
                  }}
                  aria-label={t.leaves.toYear}
                  aria-invalid={!!validationErrors[`toYear_${index}`]}
                  aria-describedby={validationErrors[`toYear_${index}`] ? `error-toYear-${index}` : undefined}
                />
                {validationErrors[`toYear_${index}`] && (
                  <div
                    id={`error-toYear-${index}`}
                    role="alert"
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "6px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <AlertCircle size={14} />
                    {validationErrors[`toYear_${index}`]}
                  </div>
                )}
                {validationErrors[`yearRange_${index}`] && !validationErrors[`toYear_${index}`] && (
                  <div
                    id={`error-yearRange-${index}`}
                    role="alert"
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "6px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <AlertCircle size={14} />
                    {validationErrors[`yearRange_${index}`]}
                  </div>
                )}
              </div>

              {/* Days */}
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.leaves.annualLeaveDays}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={rule.days}
                  onChange={(e) => {
                    if (!canEdit) return;

                    const validation = validateNumericText(e.target.value, {
                      min: 0,
                      max: 365,
                      fieldName: isRTL ? "الأيام" : "Days",
                      allowEmpty: false,
                    });

                    if (validation.isValid) {
                      const newRules = [...config.annualLeaveRules];
                      newRules[index].days = validation.sanitized;
                      setConfig({ ...config, annualLeaveRules: newRules });

                      setValidationErrors((prev) => {
                        const updated = { ...prev };
                        delete updated[`days_${index}`];
                        return updated;
                      });
                    } else {
                      setValidationErrors((prev) => ({
                        ...prev,
                        [`days_${index}`]: validation.error,
                      }));
                    }
                  }}
                  onKeyDown={restrictToNumericKeys}
                  onPaste={restrictPasteToNumeric}
                  disabled={!canEdit}
                  style={{
                    ...getFieldStyle("leaves", styles.input),
                    borderColor: validationErrors[`days_${index}`] ? "#ef4444" : undefined,
                  }}
                  placeholder="21"
                  aria-label={t.leaves.annualLeaveDays}
                  aria-invalid={!!validationErrors[`days_${index}`]}
                  aria-describedby={validationErrors[`days_${index}`] ? `error-days-${index}` : undefined}
                />
                {validationErrors[`days_${index}`] && (
                  <div
                    id={`error-days-${index}`}
                    role="alert"
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "6px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <AlertCircle size={14} />
                    {validationErrors[`days_${index}`]}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Add/Remove Rule Buttons */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginTop: "16px",
              justifyContent: isRTL ? "flex-end" : "flex-start",
              flexWrap: "wrap",
              alignItems: "stretch",
              flexDirection: typeof window !== "undefined" && window.innerWidth < 768 ? "column" : "row",
            }}
          >
            <button
              onClick={() => {
                if (!canEdit) return;
                const newRule = { yearsFrom: 10, yearsTo: null, days: 30 };
                setConfig({ ...config, annualLeaveRules: [...(config.annualLeaveRules || []), newRule] });
              }}
              disabled={!canEdit}
              style={{ ...styles.addButton, opacity: canEdit ? 1 : 0.6, cursor: canEdit ? "pointer" : "not-allowed" }}
            >
              <Plus size={18} />
              {t.leaves.addRule}
            </button>

            {(config.annualLeaveRules || []).length > 1 && (
              <button
                onClick={() => {
                  if (!canEdit) return;
                  setConfig({ ...config, annualLeaveRules: config.annualLeaveRules.slice(0, -1) });
                }}
                disabled={!canEdit}
                style={{ ...styles.removeButton, opacity: canEdit ? 1 : 0.6, cursor: canEdit ? "pointer" : "not-allowed" }}
              >
                <X size={16} />
                {t.leaves.removeRule}
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={styles.contentSection}>
        <h3 style={styles.sectionTitle}>
          <div style={styles.sectionIconBadge(PALETTE.emerald.light)}>
            <FileText size={20} />
          </div>
          {t.leaves.otherLeaveTypes}
        </h3>
        <div style={styles.formRow}>
          {/* Sick Leave */}
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.leaves.sickLeavePerYear}</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={config.sickLeavePerYear}
              onChange={(e) => {
                if (!canEdit) return;

                const validation = validateNumericText(e.target.value, {
                  min: 0,
                  max: 365,
                  fieldName: isRTL ? "الإجازة المرضية" : "Sick Leave",
                  allowEmpty: false,
                });

                if (validation.isValid) {
                  setConfig({ ...config, sickLeavePerYear: validation.sanitized });
                  setValidationErrors((prev) => {
                    const updated = { ...prev };
                    delete updated.sickLeave;
                    return updated;
                  });
                } else {
                  setValidationErrors((prev) => ({
                    ...prev,
                    sickLeave: validation.error,
                  }));
                }
              }}
              onKeyDown={restrictToNumericKeys}
              onPaste={restrictPasteToNumeric}
              disabled={!canEdit}
              style={{
                ...getFieldStyle("leaves", styles.input),
                borderColor: validationErrors.sickLeave ? "#ef4444" : undefined,
              }}
              placeholder="15"
              aria-label={t.leaves.sickLeavePerYear}
              aria-invalid={!!validationErrors.sickLeave}
              aria-describedby={validationErrors.sickLeave ? "error-sickLeave" : undefined}
            />
            {validationErrors.sickLeave && (
              <div
                id="error-sickLeave"
                role="alert"
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  marginTop: "6px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <AlertCircle size={14} />
                {validationErrors.sickLeave}
              </div>
            )}
          </div>

          {/* Free Sick Days */}
          <div style={styles.formGroup}>
            <label style={styles.label}>{isRTL ? "أيام المرضية المدفوعة بالكامل" : "Free Sick Days"}</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={config.freeSickDays ?? ""}
              onChange={(e) => {
                if (!canEdit) return;

                const validation = validateNumericText(e.target.value, {
                  min: 0,
                  max: 365,
                  fieldName: isRTL ? "أيام المرضية المدفوعة بالكامل" : "Free Sick Days",
                  allowEmpty: true,
                });

                if (validation.isValid) {
                  setConfig({ ...config, freeSickDays: validation.sanitized === "" ? null : validation.sanitized });
                  setValidationErrors((prev) => {
                    const updated = { ...prev };
                    delete updated.freeSickDays;
                    return updated;
                  });
                } else {
                  setValidationErrors((prev) => ({
                    ...prev,
                    freeSickDays: validation.error,
                  }));
                }
              }}
              onKeyDown={restrictToNumericKeys}
              onPaste={restrictPasteToNumeric}
              disabled={!canEdit}
              style={{
                ...getFieldStyle("leaves", styles.input),
                borderColor: validationErrors.freeSickDays ? "#ef4444" : undefined,
              }}
              placeholder="3"
              aria-label={isRTL ? "أيام المرضية المدفوعة بالكامل" : "Free Sick Days"}
              aria-invalid={!!validationErrors.freeSickDays}
              aria-describedby={validationErrors.freeSickDays ? "error-freeSickDays" : undefined}
            />
            {validationErrors.freeSickDays && (
              <div
                id="error-freeSickDays"
                role="alert"
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  marginTop: "6px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <AlertCircle size={14} />
                {validationErrors.freeSickDays}
              </div>
            )}
          </div>

          {/* Casual Leave */}
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.leaves.casualLeavePerYear}</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={config.casualLeavePerYear}
              onChange={(e) => {
                if (!canEdit) return;

                const validation = validateNumericText(e.target.value, {
                  min: 0,
                  max: 365,
                  fieldName: isRTL ? "الإجازة العارضة" : "Casual Leave",
                  allowEmpty: false,
                });

                if (validation.isValid) {
                  setConfig({ ...config, casualLeavePerYear: validation.sanitized });
                  setValidationErrors((prev) => {
                    const updated = { ...prev };
                    delete updated.casualLeave;
                    return updated;
                  });
                } else {
                  setValidationErrors((prev) => ({
                    ...prev,
                    casualLeave: validation.error,
                  }));
                }
              }}
              onKeyDown={restrictToNumericKeys}
              onPaste={restrictPasteToNumeric}
              disabled={!canEdit}
              style={{
                ...getFieldStyle("leaves", styles.input),
                borderColor: validationErrors.casualLeave ? "#ef4444" : undefined,
              }}
              placeholder="7"
              aria-label={t.leaves.casualLeavePerYear}
              aria-invalid={!!validationErrors.casualLeave}
              aria-describedby={validationErrors.casualLeave ? "error-casualLeave" : undefined}
            />
            {validationErrors.casualLeave && (
              <div
                id="error-casualLeave"
                role="alert"
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  marginTop: "6px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <AlertCircle size={14} />
                {validationErrors.casualLeave}
              </div>
            )}
          </div>
        </div>

        <div style={styles.toggleContainer}>
          <label style={styles.label}>{t.leaves.leaveApprovalRequired}</label>
          <ToggleSwitch
            checked={config.leaveApprovalRequired}
            onChange={(e) =>
              canEdit && setConfig({ ...config, leaveApprovalRequired: e.target.checked })
            }
            disabled={!canEdit}
            theme={theme}
            accentColor={PALETTE.emerald.base}
            isRTL={isRTL}
          />
        </div>

        <div style={styles.toggleContainer}>
          <label style={styles.label}>{t.leaves.carryOverLeaves}</label>
          <ToggleSwitch
            checked={config.carryOverLeaves}
            onChange={(e) =>
              canEdit && setConfig({ ...config, carryOverLeaves: e.target.checked })
            }
            disabled={!canEdit}
            theme={theme}
            accentColor={PALETTE.emerald.base}
            isRTL={isRTL}
          />
        </div>
      </div>
    </>
  );
};

export default LeavesTab;